/**
 * Messages bridge для frontend (feature 020).
 *
 * Загружает `config/messages.yaml` (single source of truth) через Vite `?raw` import
 * и парсит YAML в типизированный объект. Это устраняет дублирование строк между
 * backend (`@efes/config` через `node:fs`) и frontend (где `node:fs` недоступен).
 *
 * Принцип Constitution VII: «никаких string literals в коде» — все user-facing
 * строки берутся из `messages.yaml`.
 */
import yamlSource from "@efes-config/messages.yaml?raw";
import { load } from "js-yaml";

// ─── Reconciliation result UI (фича 023) ───────────────────────────
// Подсекции `reconciliation.result.*` заполняются по фазам:
// US1 (фаза 3): run, banner, discrepancy, violation, last_run.
// US2 (фаза 4): filter, drill.
// US3 (фаза 5): empty, failed, stale, limit.
// US4 (фаза 6): concurrent.
// Незаполненные подсекции типизированы как `Record<string, never>` —
// доступ к их полям вызовет ошибку TS, защищая от ранних ссылок.

export interface ResultRunMessages {
	button_label: string;
	button_disabled_running: string;
	progress_phase_default: string;
	progress_elapsed_template: string;
	rerun_dialog_title: string;
	rerun_dialog_message: string;
	rerun_dialog_confirm: string;
	rerun_dialog_cancel: string;
	toast_started: string;
	failed_title: string;
	failed_open_logs: string;
	failed_retry: string;
	failed_close: string;
}

export interface ResultBannerMessages {
	set_name_label: string;
	level_label: string;
	modes_label: string;
	started_at_label: string;
	duration_label: string;
	author_label: string;
	totals_label: string;
	totals_template: string;
	shown_template: string;
	duration_seconds_template: string;
	duration_minutes_template: string;
}

export interface ResultDiscrepancyMessages {
	column_workorders: string;
	column_violations: string;
	column_acts: string;
	no_workorder_sources: string;
	no_act_sources: string;
	show_all_sources_template: string;
	hide_extra_sources: string;
	level_position: string;
	level_grand_total: string;
	source_block_template: string;
	source_row_template: string;
}

export interface ResultViolationMessages {
	severity_error: string;
	severity_warning: string;
	expected_label: string;
	actual_label: string;
}

export interface ResultLastRunMessages {
	label: string;
	open_results_cta: string;
	failed_title: string;
	failed_retry: string;
	not_yet_run: string;
	completed_at_template: string;
	duration_label: string;
}

export interface ResultFilterMessages {
	severity_label: string;
	severity_error: string;
	severity_warning: string;
	level_label: string;
	level_position: string;
	level_grand_total: string;
	search_placeholder: string;
	search_clear_aria: string;
	reset_cta: string;
	empty_filtered_title: string;
	empty_filtered_cta: string;
}

export interface ResultDrillMessages {
	related_title_template: string;
	related_cta: string;
	target_hidden_toast: string;
	target_hidden_cta: string;
	expand_all: string;
	collapse_all: string;
}

export interface ResultEmptyMessages {
	title: string;
	subtitle: string;
	close_cta: string;
}

export interface ResultFailedMessages {
	title: string;
	open_logs_cta: string;
	retry_cta: string;
	close_cta: string;
	retry_dialog_title: string;
	retry_dialog_message: string;
	retry_dialog_prepare_cta: string;
	retry_dialog_run_as_is_cta: string;
	retry_dialog_cancel: string;
}

export interface ResultStaleMessages {
	banner_title: string;
	open_actual_cta: string;
}

export interface ResultLimitMessages {
	title_template: string;
	subtitle: string;
	open_logs_cta: string;
	close_cta: string;
}

export interface ResultConcurrentMessages {
	toast: string;
}

/** Фича 024 — редизайн карточки расхождений. */
export interface ResultRedesignMessages {
	// Σ-strip.
	sigma_sources_label: string;
	sigma_volume_label: string;
	sigma_cost_label: string;
	sigma_period_label: string;
	sigma_in_files_template: string;
	// Aggregated card.
	aggregated_subtitle_template: string;
	aggregated_toggle_label: string;
	aggregated_sort_hint: string;
	aggregated_kbd_sort: string;
	aggregated_kbd_filter: string;
	aggregated_table_col_file: string;
	aggregated_table_col_row: string;
	aggregated_table_col_volume_template: string;
	aggregated_table_col_cost: string;
	aggregated_table_block_template: string;
	aggregated_table_total_label: string;
	// Δ-pills.
	delta_volume_label: string;
	delta_cost_label: string;
	delta_rate_label: string;
	delta_rate_template: string;
	delta_name_label: string;
	delta_name_similarity_template: string;
	delta_pill_separator: string;
	// Matrix-card.
	matrix_title_template: string;
	matrix_subtitle_template: string;
	matrix_side_badge_wo: string;
	matrix_side_badge_act: string;
	matrix_col_source: string;
	matrix_col_name: string;
	matrix_col_volume: string;
	matrix_col_rate: string;
	matrix_col_cost: string;
	matrix_group_wo_template: string;
	matrix_group_act_template: string;
	matrix_total_wo: string;
	matrix_total_act: string;
	matrix_delta_label: string;
	matrix_delta_rule_template: string;
	// Hover-actions. Sprint 5 / B1: action_copy_link / action_mute /
	// action_copy_link_done / unmute_all_template удалены вместе с client-side mute.
	action_open_excel: string;
	// Master-detail.
	master_count_template: string;
	master_search_placeholder: string;
	master_grouped_label: string;
	master_ungrouped_label: string;
	master_keyboard_hint: string;
	master_pinned_group_label: string;
	master_empty_filter: string;
	master_sources_template: string;
	detail_position_template: string;
	detail_pin_aria: string;
	detail_unpin_aria: string;
	detail_open_aria: string;
	detail_prev_aria: string;
	detail_next_aria: string;
	detail_footer_hint: string;
	// Grand-total card (RC-G1).
	grand_total_title: string;
	grand_total_subtitle_template: string;
	grand_total_sigma_wo: string;
	grand_total_sigma_act: string;
	grand_total_sigma_diff: string;
	grand_total_suspect_section_template: string;
	grand_total_suspect_empty: string;
	grand_total_matched_collapsed_template: string;
	grand_total_matched_expanded_template: string;
	grand_total_table_col_code: string;
	grand_total_table_col_wo: string;
	grand_total_table_col_act: string;
	grand_total_table_col_delta: string;
	grand_total_table_col_lines: string;
	grand_total_uncoded_label: string;
	// Layout toggle.
	layout_toggle_label: string;
	layout_feed: string;
	layout_master_detail: string;
}

export interface ReconciliationResultMessages {
	run: ResultRunMessages;
	banner: ResultBannerMessages;
	discrepancy: ResultDiscrepancyMessages;
	violation: ResultViolationMessages;
	last_run: ResultLastRunMessages;
	filter: ResultFilterMessages;
	drill: ResultDrillMessages;
	empty: ResultEmptyMessages;
	failed: ResultFailedMessages;
	stale: ResultStaleMessages;
	limit: ResultLimitMessages;
	concurrent: ResultConcurrentMessages;
	redesign: ResultRedesignMessages;
}

export interface ReconciliationMessages {
	// Описания действий в журнале
	set_created: string;
	set_updated: string;
	set_deleted: string;
	// Pre-flight issues
	preflight_no_workorders: string;
	preflight_no_acts: string;
	preflight_no_modes: string;
	preflight_workorder_not_parsed: string;
	preflight_act_not_v3: string;
	preflight_act_arithmetic_failed: string;
	preflight_no_section: string;
	preflight_level_modes_incompatible: string;
	// API errors
	error_set_not_found: string;
	error_status_running: string;
	error_invalid_role: string;
	error_file_not_found: string;
	error_file_role_mismatch: string;
	error_min_workorders_acts: string;
	error_empty_patch: string;
	error_auth_required: string;
	error_run_not_found: string;
	// Empty states
	list_empty_title: string;
	list_empty_subtitle: string;
	list_empty_cta: string;
	eligible_empty_workorders: string;
	eligible_empty_acts: string;
	eligible_empty_action_files: string;
	eligible_empty_action_files_short: string;
	eligible_empty_action_logs: string;
	// UI labels
	page_list_title: string;
	page_create_title: string;
	page_edit_title: string;
	page_detail_title: string;
	button_create_set: string;
	button_save: string;
	button_cancel: string;
	button_run: string;
	button_run_disabled_tooltip: string;
	button_edit: string;
	button_delete: string;
	button_retry: string;
	delete_confirm_title: string;
	delete_confirm_message: string;
	unknown_user: string;
	status_draft: string;
	status_running: string;
	status_completed: string;
	status_failed: string;
	level_unified: string;
	level_position: string;
	level_aggregate_by_work_kind: string;
	level_aggregate_by_work_kind_v2_hint: string;
	level_grand_total: string;
	mode_cost_description: string;
	mode_volume_description: string;
	mode_rate_description: string;
	mode_rate_warning: string;
	form_advanced_summary: string;
	form_advanced_hint_default: string;
	form_advanced_hint_custom: string;
	mode_volume: string;
	mode_rate: string;
	mode_cost: string;
	toast_set_created: string;
	toast_set_updated: string;
	toast_set_deleted: string;
	toast_validation_error: string;
	toast_preflight_check_failed: string;
	form_label_name: string;
	form_placeholder_name: string;
	form_label_level: string;
	form_label_modes: string;
	form_label_workorders: string;
	form_label_acts: string;
	form_min_files_warning: string;
	form_search_placeholder: string;
	list_filter_status_label: string;
	list_filter_status_all: string;
	list_column_name: string;
	list_column_level: string;
	list_column_modes: string;
	list_column_status: string;
	list_column_author: string;
	list_column_created_at: string;
	// UI common labels (T-frontend-cleanup)
	loading: string;
	pagination_prev: string;
	pagination_next: string;
	cancel: string;
	actions_label: string;
	select_all: string;
	no_search_results: string;
	selected_count: string;
	column_file: string;
	column_sheet: string;
	column_object: string;
	column_period: string;
	column_created: string;
	tab_title_list: string;
	tab_title_create: string;
	tab_title_edit: string;
	tab_title_result: string;
	sidebar_label: string;
	sidebar_label_mobile: string;
	sidebar_label_verification: string;
	sidebar_label_verification_mobile: string;
	// Toast generic
	toast_api_error: string;
	// API errors (frontend fallbacks)
	error_create_failed: string;
	error_update_failed: string;
	error_delete_failed: string;
	error_load_failed: string;
	error_load_one_failed: string;
	error_eligible_failed: string;
	error_validate_failed: string;
	error_prepare_failed: string;
}

/** Per-rule message bundle. */
export interface RuleMessage {
	name: string;
	label: string;
	summary_template?: string;
	message_template?: string;
	[key: string]: string | undefined;
}

interface MessagesYaml {
	reconciliation: ReconciliationMessages & {
		result: ReconciliationResultMessages;
		rules: Record<string, RuleMessage>;
	};
	[key: string]: unknown;
}

const parsed = load(yamlSource) as MessagesYaml;

/** Все сообщения из `config/messages.yaml`. */
export const messages = parsed;

/**
 * Сообщения flat-уровня секции `reconciliation` (фичи 020, 022).
 * Не включает вложенную секцию `result.*` — она экспортируется отдельно
 * через `reconciliationResultMessages`, чтобы не нарушить существующие
 * `reconciliationMessages[dynamicKey]` lookup'ы (фичи 020/022 используют этот паттерн).
 */
export const reconciliationMessages: ReconciliationMessages = parsed.reconciliation;

/**
 * Сообщения вложенной секции `reconciliation.result.*` (фича 023, этап 7b).
 * Содержит подсекции `run`, `banner`, `discrepancy`, `violation`, `last_run`
 * и пустые placeholder'ы для `filter`/`drill`/`empty`/`failed`/`stale`/`limit`/`concurrent`,
 * заполняемые в US2/US3/US4.
 */
export const reconciliationResultMessages: ReconciliationResultMessages =
	parsed.reconciliation.result;

/**
 * Сообщения per-rule из секции `reconciliation.rules.*`.
 * Ключ — `ruleId.toLowerCase()` (например, "rc-p1"). Используется для
 * получения human-readable `name`/`label` правила в UI карточек (фича 024).
 */
export const reconciliationRuleMessages: Record<string, RuleMessage> = parsed.reconciliation.rules;

/**
 * Сообщения секции `pdf_export` (фича 034). Используются в кнопке скачивания
 * PDF, toast'ах об ошибках и UI-метках. Плоский Record<string, string> —
 * `glossary_entries` (вложенная map) для frontend не нужны, они отрисовываются
 * на бэкенде в PDF.
 */
export const pdfExportMessages: Record<string, string> = (parsed as Record<string, unknown>)
	.pdf_export as Record<string, string>;

/**
 * Сообщения плоской секции `ui`. Фича 038 читает отсюда разделитель
 * подписи «файл · лист» (`file_sheet_separator`).
 */
export const uiMessages: Record<string, string> = (parsed as Record<string, unknown>).ui as Record<
	string,
	string
>;

/**
 * Сообщения секции `completion_act` (используем подсекции `llm_review` и
 * `llm_review_reason` для секции «LLM-ревью разбора блоков» — фича 035).
 */
export interface CompletionActLlmReviewMessages {
	section_title: string;
	section_subtitle: string;
	empty_state: string;
	counter_applied: string;
	counter_applied_hint: string;
	counter_skipped_concern: string;
	counter_skipped_concern_hint: string;
	counter_skipped_routine: string;
	counter_skipped_routine_hint: string;
	counter_failed: string;
	counter_failed_hint: string;
	counter_pending: string;
	counter_pending_hint: string;
	counter_none: string;
	counter_none_hint: string;
	table_col_block: string;
	table_col_section: string;
	table_col_status: string;
	table_col_reason: string;
	table_col_at: string;
	status_none: string;
	status_pending: string;
	status_applied: string;
	status_skipped: string;
	status_failed: string;
	block_no_header: string;
	expand_hint: string;
	expand_what_llm_saw: string;
	expand_proposed_works: string;
	expand_proposed_works_empty: string;
	expand_discarded_rows: string;
	expand_discarded_rows_empty: string;
	expand_discarded_extras: string;
	expand_why_not_applied: string;
	expand_show_raw_button: string;
	expand_hide_raw_button: string;
	expand_copy_raw: string;
	expand_copy_raw_done: string;
	expand_loading_raw: string;
	expand_load_error: string;
	expand_block_confidence_high: string;
	expand_block_confidence_low: string;
	expand_work_confidence_high: string;
	expand_work_confidence_low: string;
	expand_absorbed_rows: string;
	expand_work_field_code: string;
	expand_work_field_name: string;
	expand_work_field_unit: string;
	expand_work_field_quantity: string;
	expand_work_field_rate: string;
	expand_work_field_total: string;
	icon_tooltip_pending: string;
	icon_tooltip_failed: string;
	icon_tooltip_concern: string;
	icon_tooltip_applied: string;
	icon_tooltip_routine: string;
}

const completionActSection = (parsed as Record<string, unknown>).completion_act as Record<
	string,
	unknown
>;

export const llmReviewMessages: CompletionActLlmReviewMessages =
	completionActSection.llm_review as CompletionActLlmReviewMessages;

export const llmReviewReasonMessages: Record<string, string> =
	completionActSection.llm_review_reason as Record<string, string>;

/**
 * Возвращает читабельное имя правила по `ruleId` (например, "RC-P1" → «Лишняя позиция в акте»).
 * Fallback на сам `ruleId`, если правило не найдено.
 *
 * Workaround для бага в `apps/api/src/services/reconciliation-runs/persistence.ts:252-253`,
 * где `ruleLabel`/`ruleName` хардкодятся в `ruleId` при загрузке из БД.
 */
export function getRuleDisplayName(ruleId: string): string {
	return reconciliationRuleMessages[ruleId.toLowerCase()]?.name ?? ruleId;
}

/** Возвращает короткий label (например «RC-P1») по `ruleId`. */
export function getRuleDisplayLabel(ruleId: string): string {
	return reconciliationRuleMessages[ruleId.toLowerCase()]?.label ?? ruleId;
}

/**
 * Подставляет значения в шаблон `{key}`.
 *
 * @example
 * formatMessage("Файл «{name}» удалён", { name: "report.xlsx" })
 * // → 'Файл «report.xlsx» удалён'
 */
export function formatMessage(template: string, values: Record<string, string | number>): string {
	let result = template;
	for (const [key, value] of Object.entries(values)) {
		result = result.replaceAll(`{${key}}`, String(value));
	}
	return result;
}
